import { ApiError, NetworkError } from '../api/client';

/** What the login screens show. Mapping follows spec §5.1–§5.2. */
export type AuthResult =
  | { kind: 'signedIn'; token: string }
  | { kind: 'needPhone' }
  | { kind: 'phoneNotConfirmed' }
  | { kind: 'invalidInitData' }
  | { kind: 'expired' }
  | { kind: 'unsupportedPhone'; message: string }
  | { kind: 'rateLimited' }
  | { kind: 'offline' }
  | { kind: 'failed' };

export interface AuthDeps {
  /** POST /tg-miniapp/v1/auth/login with the current initData; resolves with the token. */
  login: () => Promise<string>;
  sleep: (ms: number) => Promise<void>;
}

const TRY_AGAIN_DELAY_MS = 2000;
const TRY_AGAIN_RETRIES = 3;
const CONFIRM_POLL_MS = 1000;
const CONFIRM_ATTEMPTS = 10;

function toResult(error: unknown): AuthResult {
  if (error instanceof NetworkError) return { kind: 'offline' };
  if (!(error instanceof ApiError)) return { kind: 'failed' };
  switch (error.code) {
    case 'TELEGRAM_PHONE_NOT_CONFIRMED':
    case 'MAX_PHONE_NOT_CONFIRMED':
      return { kind: 'needPhone' };
    case 'INVALID_INIT_DATA':
      return { kind: 'invalidInitData' };
    case 'INIT_DATA_EXPIRED':
      return { kind: 'expired' };
    case 'TELEGRAM_PHONE_UNSUPPORTED':
    case 'MAX_PHONE_UNSUPPORTED':
      return { kind: 'unsupportedPhone', message: error.message };
    case 'REQUESTS_PER_MINUTE_LIMIT_EXCEEDED':
      return { kind: 'rateLimited' };
    default:
      // VALIDATION_ERROR means our request is broken; unknown codes are treated the same.
      return { kind: 'failed' };
  }
}

const isTryAgain = (error: unknown) =>
  error instanceof ApiError && (error.code === 'TG_MINIAPP_TRY_AGAIN' || error.code === 'MAX_MINIAPP_TRY_AGAIN');

/** Sign in on every launch; TG_MINIAPP_TRY_AGAIN is retried silently. */
export async function signIn({ login, sleep }: AuthDeps): Promise<AuthResult> {
  for (let attempt = 0; ; attempt++) {
    try {
      return { kind: 'signedIn', token: await login() };
    } catch (error) {
      if (isTryAgain(error) && attempt < TRY_AGAIN_RETRIES) {
        await sleep(TRY_AGAIN_DELAY_MS);
        continue;
      }
      return toResult(error);
    }
  }
}

/**
 * After requestContact: the phone reaches the server through the bot in 1–3 s,
 * so poll once a second for at most 10 s.
 */
export async function confirmPhone({ login, sleep }: AuthDeps): Promise<AuthResult> {
  for (let attempt = 0; attempt < CONFIRM_ATTEMPTS; attempt++) {
    await sleep(CONFIRM_POLL_MS);
    try {
      return { kind: 'signedIn', token: await login() };
    } catch (error) {
      const result = toResult(error);
      if (result.kind === 'needPhone' || isTryAgain(error)) continue;
      return result;
    }
  }
  return { kind: 'phoneNotConfirmed' };
}

/**
 * Max: the signed phone goes straight to mobile-api, which answers with the token, so there is nothing to poll.
 * A phone the server would not accept as the resident's own reads like an unconfirmed one: share again.
 */
export async function confirmContact(confirm: () => Promise<string>, sleep: AuthDeps['sleep']): Promise<AuthResult> {
  const result = await signIn({ login: confirm, sleep });
  return result.kind === 'needPhone' ? { kind: 'phoneNotConfirmed' } : result;
}
